import {
  useMutation,
  UseMutationOptions,
  useQueryClient,
} from '@tanstack/react-query';

import { gqlClient } from '@/api/graphql-client';
import meKeys from '@/api/queries/me/keyFactory';
import { graphql } from '@/gql';
import { AddTeamMemberMutation, TeamMemberInput } from '@/gql/graphql';
import {
  applyOptimisticMembership,
  MembersMutationContext,
  rollbackOptimisticMembership,
} from './membershipCache';

const ADD_TEAM_MEMBER_MUTATION = graphql(`
  mutation AddTeamMember($input: TeamMemberInput!) {
    addTeamMember(input: $input) {
      id
    }
  }
`);

const useAddTeamMember = (
  options?: UseMutationOptions<
    AddTeamMemberMutation,
    unknown,
    TeamMemberInput,
    MembersMutationContext
  >
) => {
  const queryClient = useQueryClient();
  const { onSuccess, ...restOptions } = options ?? {};

  return useMutation({
    mutationFn: (input) => {
      return gqlClient.request(ADD_TEAM_MEMBER_MUTATION, { input });
    },
    ...restOptions,
    onMutate: ({ teamId, userId }) =>
      applyOptimisticMembership(queryClient, { teamId, userId, action: 'add' }),
    onError: (_error, _variables, context) => {
      rollbackOptimisticMembership(queryClient, context);
    },
    onSuccess,
    onSettled: () => {
      // Membership drives the users list, the Teams list counts and each user's
      // teams, all sourced from the account users query. Reconcile on settle
      // (not just success) so a failed mutation's rollback — which restores a
      // snapshot that may be stale if a concurrent membership change landed in
      // between — can't leave the cache wrong.
      void queryClient.invalidateQueries({
        queryKey: meKeys.accountUsers(),
      });
    },
  });
};

export default useAddTeamMember;
